import { useTranslation } from 'react-i18next';
import { Tooltip } from '@components/ui/Tooltip/Tooltip';
import { CpuIcon, PencilIcon } from '@utils/icons';
import { useLogisticsEditLock } from '../../revision/LogisticsEditLock';
import { EquipmentCatalog } from './EquipmentCatalog';
import type { useAreaDrawing } from './useAreaDrawing';
import type { SiteMapMode, useEquipmentPlacement } from './useEquipmentPlacement';
import styles from './SiteMap.module.css';

interface ISiteMapEditToolsProps {
  /** A plan is configured — there is a map to draw or place on. */
  hasPlan: boolean;
  mode: SiteMapMode;
  drawing: Pick<ReturnType<typeof useAreaDrawing>, 'start' | 'resetDraw'>;
  equipmentPlacement: Pick<
    ReturnType<typeof useEquipmentPlacement>,
    'catalogOpen' | 'setCatalogOpen' | 'pick' | 'reset'
  >;
}

/** The site-map toolbar's write tools — draw an area, place equipment — and
 *  the divider after them. Rendered only for a writer; disabled while the
 *  page's edit lock holds. */
export function SiteMapEditTools({ hasPlan, mode, drawing, equipmentPlacement }: ISiteMapEditToolsProps) {
  const { t } = useTranslation();
  const { locked } = useLogisticsEditLock();
  return (
    <>
      {/* ui/Tooltip's hint variant, not a native `title` — the styled
          replacement frontend/CLAUDE.md §5 mandates for explanatory hover
          text on an icon-only control. */}
      <Tooltip content={t('logistics.siteMap.drawArea')}>
      <button
        type="button"
        className={`${styles.toolbarBtn} ${styles.toolbarBtnPrimary} ${styles.toolbarBtnIcon}`}
        aria-label={t('logistics.siteMap.drawArea')}
        disabled={!hasPlan || locked}
        data-active={mode === 'draw' || undefined}
        onClick={(e) => {
          // Second click while drawing cancels, exactly like Esc.
          if (mode === 'draw') drawing.resetDraw();
          else drawing.start();
          // A focused <button> re-fires its own click on Enter — exactly the
          // key that closes the polygon. Blur so the first Enter reaches
          // only the window-level draw-mode listener, not this button too.
          e.currentTarget.blur();
        }}
      >
        <PencilIcon size={13} />
      </button>
      </Tooltip>
      <div className={styles.placeEquipmentWrap}>
        <Tooltip content={t('logistics.siteMap.placeEquipment')}>
        <button
          type="button"
          className={`${styles.toolbarBtn} ${styles.toolbarBtnSecondary} ${styles.toolbarBtnIcon}`}
          aria-label={t('logistics.siteMap.placeEquipment')}
          disabled={!hasPlan || locked}
          data-active={mode === 'placeEquipment' || mode === 'setRadius' || undefined}
          onClick={(e) => {
            // Second click while a placement is in flight cancels it, exactly
            // like Esc; otherwise the button just toggles the catalog.
            if (mode === 'placeEquipment' || mode === 'setRadius') {
              equipmentPlacement.reset();
            } else {
              equipmentPlacement.setCatalogOpen((v) => !v);
            }
            e.currentTarget.blur();
          }}
        >
          <CpuIcon size={13} />
        </button>
        </Tooltip>
        <EquipmentCatalog
          open={equipmentPlacement.catalogOpen}
          onPick={equipmentPlacement.pick}
          onClose={() => equipmentPlacement.setCatalogOpen(false)}
        />
      </div>
      <div className={styles.toolbarDivider} aria-hidden="true" />
    </>
  );
}
